import React from "react";
import { Search, CheckCircle2, Circle, Clock } from "lucide-react";
import { formatMoney } from "../../lib/format";
const MOCK_TOTAL = { BRL: 2500, USD: 800, EUR: 700 };

// Realistic "inside the product" mockups for the V2 landing. Pure UI, no backend calls.
const Frame = ({ title, children, testid }) => (
  <div className="overflow-hidden rounded-2xl border border-xmas-gold/25 bg-[#0f1530] shadow-2xl shadow-black/40" data-testid={testid}>
    <div className="flex items-center gap-2 border-b border-white/5 bg-white/[0.03] px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-3 font-display text-xs tracking-widest text-xmas-gold">{title}</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

export function DashboardMock({ C, current, lang, compact = false }) {
  const D = C.dash;
  const facts = [[D.country, `${current?.flag || "🇧🇷"} ${current?.name?.[lang] || "Brasil"}`], [D.guests, "10"], [D.budget, formatMoney(current?.symbol || "R$", MOCK_TOTAL[current?.currency] || 2500)], [D.style, D.styleV], [D.ss, D.yes], [D.plan, D.planV]];
  return (
    <Frame title={`🎄 ${D.title}`} testid="v2-mock-dashboard">
      <div className={`grid gap-2 ${compact ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3"}`}>
        {facts.map(([k, v]) => (
          <div key={k} className="rounded-xl border border-white/5 bg-white/[0.04] px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-xmas-slate">{k}</p>
            <p className="truncate text-sm font-semibold text-xmas-snow">{v}</p>
          </div>
        ))}
      </div>
      <div className={`mt-3 grid gap-2 ${compact ? "grid-cols-4" : "grid-cols-2 sm:grid-cols-4"}`}>
        {D.cards.map((c) => (
          <div key={c} className="rounded-xl border border-xmas-gold/15 bg-gradient-to-br from-xmas-gold/10 to-transparent px-3 py-3 text-center text-xs font-medium text-xmas-champagne">{c}</div>
        ))}
      </div>
    </Frame>
  );
}

export function PlanningMock({ C }) {
  const P = C.planning;
  const Icon = { feito: CheckCircle2, andamento: Clock, pendente: Circle };
  const color = { feito: "text-emerald-400", andamento: "text-xmas-gold", pendente: "text-xmas-slate" };
  return (
    <Frame title={`📅 ${P.title}`} testid="v2-mock-planning">
      <ul className="space-y-2">
        {P.items.map(([cat, task, st]) => { const I = Icon[st]; return (
          <li key={cat} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.04] px-3 py-2.5">
            <I className={`h-4 w-4 shrink-0 ${color[st]}`} />
            <div className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-widest text-xmas-slate">{cat}</p><p className="truncate text-sm text-xmas-snow">{task}</p></div>
            <span className={`shrink-0 rounded-full border border-current px-2 py-0.5 text-[10px] ${color[st]}`}>{P.status[st]}</span>
          </li>); })}
      </ul>
    </Frame>
  );
}

export function SearchMock({ C, current, lang }) {
  const S = C.search;
  const sym = current?.symbol || "R$";
  const prices = current?.currency === "BRL" ? [299, 89, 59, 39] : [79, 24, 15, 12];
  const filters = S.filters.map((f) => f.replace("{country}", `${current?.flag || ""} ${current?.name?.[lang] || ""}`.trim()).replace("{sym}", sym));
  return (
    <Frame title={`🔎 ${S.title}`} testid="v2-mock-search">
      <div className="flex gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-xmas-gold/30 bg-xmas-navy/70 px-4 py-2.5 text-sm text-xmas-snow"><Search className="h-4 w-4 text-xmas-gold" />{S.q}<span className="ml-auto text-xs text-xmas-slate">{S.ph}</span></div>
        <span className="btn-gold rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-wider">{S.btn}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">{filters.map((f) => <span key={f} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-xmas-champagne">{f}</span>)}</div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {S.results.map((r, i) => (
          <div key={r} className="rounded-xl border border-white/5 bg-white/[0.04] p-2.5">
            <div className="mb-2 h-16 rounded-lg bg-gradient-to-br from-xmas-gold/25 via-white/10 to-transparent" />
            <p className="truncate text-xs text-xmas-snow">{r}</p>
            <p className="font-mono-num text-sm font-semibold text-xmas-gold">{formatMoney(sym, prices[i])}</p>
            <p className="text-[10px] text-xmas-slate">{S.store}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-[10px] text-xmas-slate">{S.demo}</p>
    </Frame>
  );
}

export function BudgetMock({ C, current }) {
  const B = C.budget;
  const total = MOCK_TOTAL[current?.currency] || 2500;
  const usedPct = [60, 45, 30, 80];
  const cur = current?.currency || "BRL"; const sym = current?.symbol || "R$";
  const used = B.cats.reduce((a, [, pct], i) => a + (total * pct / 100) * usedPct[i] / 100, 0);
  return (
    <Frame title={`💰 ${B.title}`} testid="v2-mock-budget">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div><p className="text-[10px] uppercase tracking-widest text-xmas-slate">{B.total}</p><p className="font-display text-3xl font-black text-xmas-gold">{formatMoney(sym, total)}</p></div>
        <div className="text-right"><p className="text-[10px] uppercase tracking-widest text-xmas-slate">{B.left}</p><p className="font-mono-num text-xl font-semibold text-emerald-400">{formatMoney(sym, Math.round(total - used))}</p><p className="text-[11px] text-xmas-slate">{Math.round(used / total * 100)}% {B.used}</p></div>
      </div>
      <div className="mt-4 space-y-2.5">
        {B.cats.map(([name, pct], i) => (
          <div key={name}>
            <div className="mb-1 flex justify-between text-xs"><span className="text-xmas-champagne">{name}</span><span className="font-mono-num text-xmas-slate">{formatMoney(sym, Math.round(total * pct / 100))} · {usedPct[i]}%</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-xmas-gold to-[#f3d27a]" style={{ width: `${usedPct[i]}%` }} /></div>
          </div>
        ))}
      </div>
    </Frame>
  );
}
